import { getReviews } from '../lib/api';

/** =========================================
 *  Marquee infinito por scrollLeft
 *  - Duplica el contenido para poder “envolver”
 *  - Avanza con requestAnimationFrame
 *  - Pausa al pasar el ratón y tras scroll/touch del usuario
 *  - Respeta prefers-reduced-motion
 *  ========================================= */

type MarqueeOpts = {
  /** Velocidad en píxeles por segundo (default: 40) */
  pxPerSecond?: number;
  /** Pausa tras interacción del usuario (ms) (default: 2500) */
  pauseAfterUserScrollMs?: number;
};

function setupInfiniteMarquee(track: HTMLElement, opts: MarqueeOpts = {}) {
  const speed = Math.max(10, opts.pxPerSecond ?? 40);      // velocidad mínima razonable
  const pauseAfterUserScrollMs = opts.pauseAfterUserScrollMs ?? 2500;
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Si no hay tarjetas o el usuario prefiere menos animación, no iniciar
  if (track.children.length === 0 || prefersReduced) return;

  // Evita inicializar dos veces sobre el mismo track
  if (track.dataset.marquee === 'on') return;
  track.dataset.marquee = 'on';

  // Desactiva el snap para evitar tirones durante el auto-scroll
  const prevSnap = track.style.scrollSnapType;
  track.style.scrollSnapType = 'none';

  // Duplicamos las tarjetas para crear un “circuito”
  const originals = Array.from(track.children).map(n => n.cloneNode(true));
  const frag = document.createDocumentFragment();
  originals.forEach(n => frag.appendChild(n));
  track.appendChild(frag);

  // Posición inicial y estado interno
  track.scrollLeft = 0;
  let rafId = 0;
  let lastTs = 0;
  let paused = false;
  let userPauseUntil = 0; // timestamp hasta el que pausamos por interacción del usuario

  // Bucle de animación
  const tick = (ts: number) => {
    if (paused) { rafId = requestAnimationFrame(tick); return; }

    if (!lastTs) lastTs = ts;
    const dt = (ts - lastTs) / 1000; // segundos desde el frame anterior
    lastTs = ts;

    // Pausa temporal tras interacción del usuario
    if (userPauseUntil && ts < userPauseUntil) {
      rafId = requestAnimationFrame(tick);
      return;
    } else {
      userPauseUntil = 0;
    }

    // Avance
    track.scrollLeft += speed * dt;

    // Cuando consumimos la mitad (los originales), “envolvemos”
    const half = track.scrollWidth / 2;
    if (track.scrollLeft >= half) {
      track.scrollLeft -= half;
    }

    rafId = requestAnimationFrame(tick);
  };

  // Control básico de pausa/reanudación
  const start = () => {
    if (prefersReduced) return;
    if (rafId) cancelAnimationFrame(rafId);
    lastTs = 0;
    rafId = requestAnimationFrame(tick);
  };
  const stop = () => { if (rafId) cancelAnimationFrame(rafId); rafId = 0; };

  // Pausa en hover (accesibilidad/lectura)
  const onEnter = () => { paused = true; };
  const onLeave = () => { paused = false; };

  track.addEventListener('pointerenter', onEnter);
  track.addEventListener('pointerleave', onLeave);

  // Si el usuario hace scroll/touch, pausamos un rato y luego reanudamos solos
  const onUserScroll = () => { userPauseUntil = performance.now() + pauseAfterUserScrollMs; };
  track.addEventListener('wheel', onUserScroll, { passive: true });
  track.addEventListener('touchstart', onUserScroll, { passive: true });
  track.addEventListener('touchmove', onUserScroll, { passive: true });

  // Pausa cuando la pestaña no está visible
  const onVis = () => { if (document.hidden) stop(); else start(); };
  document.addEventListener('visibilitychange', onVis);

  // Iniciar
  start();

  // Función de limpieza opcional (por si desmontas el track en SPA)
  return () => {
    stop();
    track.style.scrollSnapType = prevSnap;
    track.removeEventListener('pointerenter', onEnter);
    track.removeEventListener('pointerleave', onLeave);
    track.removeEventListener('wheel', onUserScroll);
    track.removeEventListener('touchstart', onUserScroll);
    track.removeEventListener('touchmove', onUserScroll);
    document.removeEventListener('visibilitychange', onVis);
    delete track.dataset.marquee;
  };
}

/** ==========================
 *  Utils de pintado seguro
 *  ========================== */

function byId<T extends HTMLElement>(id: string) {
  return document.getElementById(id) as T | null;
}

/** Escapa HTML para prevenir XSS al usar innerHTML */
function escapeHtml(s: string) {
  return s.replace(/[&<>"']/g, m =>
    ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[m] as string)
  );
}

/** SVG de estrella (activa/inactiva) */
function starSvg(active: boolean) {
  return `<svg viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5 ${active ? 'text-yellow-400' : 'text-gray-300'}">
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 00.95.69h4.175c.969 0 1.371 1.24.588 1.81l-3.38 2.455a1 1 0 00-.364 1.118l1.287 3.966c.3.922-.755 1.688-1.54 1.118l-3.38-2.454a1 1 0 00-1.175 0l-3.38 2.454c-.784.57-1.838-.196-1.54-1.118l1.287-3.966a1 1 0 00-.364-1.118L2.05 9.394c-.783-.57-.38-1.81.588-1.81h4.175a1 1 0 00.95-.69l1.286-3.967z"/>
  </svg>`;
}

/** Tarjeta de review */
function cardHtml(review: { name: string; message: string; rating?: number }) {
  const rating = Math.max(0, Math.min(5, Number(review.rating ?? 5)));
  const stars = Array.from({ length: 5 }, (_, i) => starSvg(i < rating)).join('');
  return `
  <div class="snap-start shrink-0 w-[320px] md:w-[360px] bg-white p-6 rounded-xl shadow-lg">
    <blockquote class="text-gray-600 mb-4 italic">"${escapeHtml(review.message)}"</blockquote>
    <div class="flex items-center mb-2">${stars}</div>
    <p class="font-semibold text-gray-900">${escapeHtml(review.name)}</p>
  </div>`;
}

/** ==========================
 *  Render dinámico de reviews
 *  ========================== */
async function renderReviews() {
  const track = byId<HTMLDivElement>('testimonials-track');
  if (!track) return;

  // Skeleton simple mientras carga
  track.innerHTML = `
    <div class="snap-start shrink-0 w-[320px] md:w-[360px] bg-white p-6 rounded-xl shadow-lg animate-pulse">
      <div class="h-16 bg-gray-200 rounded mb-4"></div>
      <div class="flex gap-1 mb-2">${Array.from({length:5}).map(()=>'<div class="w-5 h-5 bg-gray-200 rounded"></div>').join('')}</div>
      <div class="h-4 bg-gray-200 rounded w-1/2"></div>
    </div>`;

  try {
    const { reviews } = await getReviews();

    if (!reviews || reviews.length === 0) {
      track.innerHTML = `<div class="text-gray-500 italic p-6">Aún no hay testimonios. Sé el primero en dejar uno.</div>`;
      return;
    }

    // Pintado de tarjetas
    track.innerHTML = reviews.map(cardHtml).join('');

    // Activa marquee infinito (ajusta velocidad si quieres)
    setupInfiniteMarquee(track, { pxPerSecond: 30, pauseAfterUserScrollMs: 2500 });

  } catch (e) {
    console.error(e);
    track.innerHTML = `<div class="text-red-600 p-6">No se pudieron cargar los testimonios ahora mismo.</div>`;
  }
}

// Ejecutar al cargar y permitir refresco desde otros módulos
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  document.addEventListener('DOMContentLoaded', renderReviews);

  // Escucha una señal global para recargar las reviews
  document.addEventListener('reviews:refresh', () => {
    renderReviews();
  });

  // (Opcional) acceso para debug desde consola
  (window as any).renderReviews = renderReviews;
}

